/**
 * Instagram Scraper — wraps the apify/instagram-scraper actor.
 * Lets the user scrape profiles/hashtags/places, persist results,
 * filter by has-email, and convert post owners into B2B leads.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import KanbanTodayBadge, { notifyLeadConverted } from "../components/KanbanTodayBadge";
import { toast } from "sonner";
import {
  Instagram, Hash, AtSign, Search, Loader2, Mail, Heart, MessageCircle,
  Eye, ExternalLink, Trash2, UserPlus, Filter, RefreshCw, Download, Square,
  Play, FileText, BookOpen,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import ScraperHelp from "../components/ScraperHelp";
import { useLang } from "../i18n/LangContext";
import { useAutoRefresh, AutoRefreshToggle } from "../hooks/useAutoRefresh";
import { useTrialLock } from "../hooks/useTrialLock";
import { blockExportIfTrial } from "../components/TrialLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InstagramScraper() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [tab, setTab] = useState("scrape");
  const [stats, setStats] = useState({ total_posts: 0, posts_with_email: 0, top_owners: [], top_hashtags: [] });

  const reloadStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/instagram/stats`); setStats(r.data); }
    catch (_e) { /* ignore */ }
  }, []);
  useEffect(() => { reloadStats(); }, [reloadStats]);
  const auto = useAutoRefresh(reloadStats, 30000, { defaultEnabled: true });

  return (
    <div className="min-h-screen" style={{ background: "var(--al-bg)" }} data-testid="ig-scraper-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)] flex flex-col">
        <header className="flex items-center gap-3 px-4 lg:px-8 h-[70px] border-b border-[var(--al-border)] bg-[var(--al-card-bg)]">
          <Logo size={28} />
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white flex items-center justify-center">
            <Instagram className="w-5 h-5" />
          </div>
          <h1 className="text-[16px] font-bold tracking-tight">
            <span className="bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] bg-clip-text text-transparent">Instagram Scraper</span>
          </h1>
          <span className="ml-3 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase">
            {isPt ? "Apify · Real" : "Apify · Real"}
          </span>
          <AutoRefreshToggle {...auto} label={isPt ? "Auto-atualizar" : "Auto-refresh"} />
          <div className="ml-auto flex items-center gap-2">
            <LangToggle />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 px-6 lg:px-10 py-6 max-w-[1320px] w-full">
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Kpi label={isPt ? "Posts coletados" : "Posts captured"} value={stats.total_posts} tone="text-[#DD2A7B]" />
            <Kpi label={isPt ? "Com e-mail" : "With email"} value={stats.posts_with_email} tone="text-emerald-600" />
            <Kpi label={isPt ? "Perfis únicos" : "Unique profiles"} value={(stats.top_owners || []).length} tone="text-[#8134AF]" />
            <Kpi label={isPt ? "Hashtags top" : "Top hashtags"} value={(stats.top_hashtags || []).length} tone="text-[#F58529]" />
          </div>

          <div className="flex gap-1 border-b border-[var(--al-border)] mb-5">
            {[
              { id: "scrape", icon: Search, en: "Scrape", pt: "Coletar" },
              { id: "results", icon: Hash, en: "Results", pt: "Resultados" },
              { id: "runs", icon: Play, en: "Runs", pt: "Execuções" },
              { id: "help", icon: BookOpen, en: "Help", pt: "Ajuda" },
            ].map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} type="button" onClick={() => setTab(t.id)}
                  data-testid={`ig-tab-${t.id}`}
                  className={`px-4 h-10 inline-flex items-center gap-2 text-[13px] font-bold rounded-t-md ${active ? "bg-[var(--al-card-bg)] border-x border-t border-[var(--al-border)] text-[#DD2A7B] -mb-px" : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"}`}>
                  <Icon className="w-3.5 h-3.5" /> {isPt ? t.pt : t.en}
                </button>
              );
            })}
          </div>

          {tab === "scrape" && <ScrapeTab isPt={isPt} onDone={() => { reloadStats(); setTab("results"); }} />}
          {tab === "results" && <ResultsTab isPt={isPt} stats={stats} onMutate={reloadStats} />}
          {tab === "runs" && <RunsTab isPt={isPt} onIngested={reloadStats} />}
          {tab === "help" && (
            <ScraperHelp
              title={isPt ? "Como usar o Instagram Scraper" : "How to use the Instagram Scraper"}
              intro={isPt
                ? "Extraia posts, comentários ou stories de perfis públicos, hashtags ou URLs do Explore. O actor Apify roda em segundo plano e devolve mídias, captions, e-mails extraídos da bio e métricas de engajamento."
                : "Pull posts, comments or stories from public profiles, hashtags or Explore URLs. The Apify actor runs in the background and returns media, captions, bio-extracted emails and engagement metrics."}
              steps={[
                {
                  title: isPt ? "1. Conecte sua conta Apify" : "1. Connect your Apify account",
                  body: isPt
                    ? "Acesse Integrations → Apify e cole seu APIFY_API_TOKEN. Sem token o scraping não funciona."
                    : "Go to Integrations → Apify and paste your APIFY_API_TOKEN. Without it scraping won't work.",
                  link: { href: "/integrations", label: isPt ? "Abrir Integrations" : "Open Integrations" },
                },
                {
                  title: isPt ? "2. Escolha o modo (Perfis, Hashtags ou URLs)" : "2. Pick a mode (Profiles, Hashtags or URLs)",
                  body: isPt
                    ? "Use 'Perfis' para @usernames, 'Hashtags' para tags (sem #) e 'URLs' para qualquer link público do Explore. Coloque até 10 entradas separadas por vírgula."
                    : "Use 'Profiles' for @usernames, 'Hashtags' for tags (no #) and 'URLs' for any public Explore link. Up to 10 entries comma-separated.",
                },
                {
                  title: isPt ? "3. Defina o tipo de resultado e limite" : "3. Choose result type and limit",
                  body: isPt
                    ? "Posts é o padrão. Para detalhes do perfil escolha 'Profile details'. Mantenha limite ≤30 para coleta sync rápida (até ~110s)."
                    : "Posts is the default. For profile details pick 'Profile details'. Keep limit ≤30 for fast sync runs (~110s).",
                },
                {
                  title: isPt ? "4. Sync vs Background" : "4. Sync vs Background",
                  body: isPt
                    ? "Sync (verde) espera o resultado e mostra direto. Background dispara um run, retorna o run_id e você acompanha em 'Execuções'."
                    : "Sync (green) waits for the result. Background fires a run and lets you track it in 'Runs'.",
                },
                {
                  title: isPt ? "5. Converta posts em leads" : "5. Convert posts into leads",
                  body: isPt
                    ? "Na aba Resultados, clique em 'Lead' em cada card para criar um lead no CRM com o e-mail extraído da bio (quando existe)."
                    : "On Results, click 'Lead' on any card to create a CRM lead populated with the bio-extracted email (when present).",
                },
              ]}
              tips={isPt ? [
                "Filtros 'Com e-mail' são ouro: vão direto para outreach.",
                "Hashtags geram mais volume; @username gera melhor qualidade.",
                "Posts antigos (>90 dias) são descartados automaticamente para reduzir ruído.",
              ] : [
                "The 'With email' filter is gold — pipe straight into outreach.",
                "Hashtags = volume; @usernames = quality.",
                "Posts older than 90 days are auto-discarded to cut noise.",
              ]}
              troubleshooting={isPt ? [
                { problem: "Imagem aparece quebrada", solution: "Estamos servindo via /api/instagram/proxy-image — recarregue. Instagram bloqueia hotlinking direto." },
                { problem: "Run fica em RUNNING", solution: "Hashtags grandes podem demorar. Acompanhe na aba Execuções e ingira quando terminar." },
                { problem: "Erro 401 na Apify", solution: "Token inválido — gere um novo em apify.com/account/integrations e cole em Integrations." },
              ] : [
                { problem: "Image is broken", solution: "We're serving it via /api/instagram/proxy-image — refresh. Instagram blocks direct hotlinking." },
                { problem: "Run stuck in RUNNING", solution: "Large hashtags take time. Watch the Runs tab and ingest once it succeeds." },
                { problem: "401 from Apify", solution: "Invalid token. Generate a new one at apify.com/account/integrations and paste it in Integrations." },
              ]}
              links={[
                { href: "https://apify.com/apify/instagram-scraper", label: "Apify · instagram-scraper" },
                { href: "https://help.instagram.com/581066165581870", label: isPt ? "Política IG (links)" : "Instagram policy (link rules)" },
              ]}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className="al-card p-4">
      <div className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold">{label}</div>
      <div className={`text-[26px] font-bold mt-1 tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

// ---- Scrape tab -----------------------------------------------------------
function ScrapeTab({ isPt, onDone }) {
  const [mode, setMode] = useState("usernames");
  const [input, setInput] = useState("");
  const [resultType, setResultType] = useState("posts");
  const [limit, setLimit] = useState(20);
  const [busy, setBusy] = useState(false);

  const placeholder = {
    usernames: "humansofny, instagram, natgeo",
    hashtags:  "blackfriday, smallbusiness, photography",
    urls:      "https://www.instagram.com/explore/locations/213385402/new-york-new-york/",
  }[mode];

  const submit = async (sync) => {
    const tokens = input.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 10);
    if (tokens.length === 0) {
      toast.error(isPt ? "Adicione ao menos 1 entrada." : "Add at least 1 entry.");
      return;
    }
    const payload = { result_type: resultType, results_limit: Number(limit), sync };
    if (mode === "usernames") payload.usernames = tokens;
    else if (mode === "hashtags") payload.hashtags = tokens.map((t) => t.replace(/^#/, ""));
    else payload.urls = tokens;

    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/instagram/scrape`, payload, { timeout: 180000 });
      if (data.mode === "sync") {
        toast.success(isPt ? `${data.inserted} novos posts (${data.emails_total} e-mails)` : `${data.inserted} new posts (${data.emails_total} emails)`);
        if (onDone) onDone();
      } else {
        toast.success(isPt ? `Run iniciada: ${data.run_id}` : `Run started: ${data.run_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="al-card p-6 space-y-5 max-w-3xl" data-testid="ig-scrape-form">
      <div className="grid grid-cols-3 gap-2 rounded-lg border border-[var(--al-border)] p-1 bg-[var(--al-row-alt)]">
        {[
          { id: "usernames", icon: AtSign, en: "Profiles", pt: "Perfis" },
          { id: "hashtags",  icon: Hash,   en: "Hashtags", pt: "Hashtags" },
          { id: "urls",      icon: ExternalLink, en: "URLs", pt: "URLs" },
        ].map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button key={m.id} type="button" onClick={() => setMode(m.id)}
              data-testid={`ig-mode-${m.id}`}
              className={`py-2 text-[12.5px] font-bold uppercase rounded inline-flex items-center justify-center gap-1.5 ${active ? "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white shadow" : "text-[var(--al-text-muted)]"}`}>
              <Icon className="w-3.5 h-3.5" /> {isPt ? m.pt : m.en}
            </button>
          );
        })}
      </div>

      <div>
        <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
          {isPt ? "Entradas (separadas por vírgula, máx 10)" : "Inputs (comma-separated, max 10)"}
        </div>
        <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder} data-testid="ig-input"
          className="w-full al-input h-10 text-[13px]" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
            {isPt ? "Tipo de resultado" : "Result type"}
          </div>
          <select value={resultType} onChange={(e) => setResultType(e.target.value)}
            data-testid="ig-result-type"
            className="w-full al-input h-9 text-[12.5px]">
            <option value="posts">{isPt ? "Posts (default)" : "Posts (default)"}</option>
            <option value="comments">{isPt ? "Comentários" : "Comments"}</option>
            <option value="stories">Stories</option>
            <option value="details">{isPt ? "Detalhes do perfil" : "Profile details"}</option>
          </select>
        </label>
        <label>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
            {isPt ? "Limite por entrada" : "Items per input"}
          </div>
          <input type="number" min="1" max="200" value={limit} onChange={(e) => setLimit(e.target.value)}
            data-testid="ig-limit"
            className="w-full al-input h-9 text-[12.5px] tabular-nums" />
        </label>
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--al-border)]">
        <button type="button" onClick={() => submit(true)} disabled={busy}
          data-testid="ig-scrape-sync"
          className="h-10 px-5 rounded-lg bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {isPt ? "Buscar agora (sync)" : "Scrape now (sync)"}
        </button>
        <button type="button" onClick={() => submit(false)} disabled={busy}
          data-testid="ig-scrape-async"
          className="h-10 px-4 rounded-lg border-2 border-[#DD2A7B] text-[#DD2A7B] hover:bg-[#DD2A7B]/10 font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-60">
          <Play className="w-4 h-4" /> {isPt ? "Rodar em background" : "Run in background"}
        </button>
      </div>

      <p className="text-[11px] text-[var(--al-text-muted)] pt-2">
        {isPt
          ? "Modo sync espera até ~110s. Para muitos posts, use background — verifique status na aba Execuções."
          : "Sync waits up to ~110s. For long jobs use background — check status on the Runs tab."}
      </p>
    </div>
  );
}

// ---- Results tab ----------------------------------------------------------
function ResultsTab({ isPt, stats, onMutate }) {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ owner: "", hashtag: "", has_email: null });
  const navigate = useNavigate();

  const load = async () => {
    const params = {};
    if (filters.owner) params.owner = filters.owner;
    if (filters.hashtag) params.hashtag = filters.hashtag.replace(/^#/, "");
    if (filters.has_email !== null) params.has_email = filters.has_email;
    params.limit = 100;
    const { data } = await axios.get(`${API}/instagram/posts`, { params });
    setItems(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const promote = async (post) => {
    try {
      const { data } = await axios.post(`${API}/instagram/posts/${post.id}/convert-to-lead`);
      notifyLeadConverted();
      // Toast with one-click link to the Kanban board so the user knows
      // exactly where the new lead landed.
      toast.success(
        isPt ? "Lead criado e enviado ao Kanban" : "Lead created and sent to Kanban",
        {
          description: isPt
            ? "Visível na coluna NEW do CRM Kanban."
            : "Visible in the NEW column of the CRM Kanban.",
          action: {
            label: isPt ? "Abrir Kanban" : "Open Kanban",
            onClick: () => navigate(data.kanban_url || "/kanban"),
          },
          duration: 6000,
        }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const { onTrial } = useTrialLock();
  const exportCsv = () => {
    if (blockExportIfTrial(onTrial, isPt ? "exportar CSV" : "export CSV")) return;
    if (items.length === 0) return;
    const header = ["owner_username","owner_full_name","type","url","caption","likes","comments","video_views","extracted_emails","timestamp"];
    const rows = items.map((p) => header.map((k) =>
      `"${String(Array.isArray(p[k]) ? p[k].join(";") : (p[k] ?? "")).replace(/"/g, '""').replace(/\n/g, " ")}"`).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `instagram_posts_${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="grid lg:grid-cols-4 gap-5" data-testid="ig-results">
      <aside className="al-card p-5 space-y-5 lg:col-span-1">
        <div>
          <h3 className="text-[14px] font-bold mb-2 inline-flex items-center gap-1.5"><Filter className="w-4 h-4 text-[#DD2A7B]" /> {isPt ? "Filtros" : "Filters"}</h3>
          <input type="text" value={filters.owner} onChange={(e) => setFilters({ ...filters, owner: e.target.value })}
            placeholder={isPt ? "Username (exato)" : "Exact username"} data-testid="ig-filter-owner"
            className="w-full al-input h-9 text-[12.5px] mb-2" />
          <input type="text" value={filters.hashtag} onChange={(e) => setFilters({ ...filters, hashtag: e.target.value })}
            placeholder="#hashtag" data-testid="ig-filter-hashtag"
            className="w-full al-input h-9 text-[12.5px] mb-2" />
          <div className="grid grid-cols-3 gap-1 text-[10.5px] font-bold uppercase">
            {[{v:null,l:isPt?"Todos":"All"},{v:true,l:isPt?"Com e-mail":"With email"},{v:false,l:isPt?"Sem e-mail":"No email"}].map((o) => (
              <button key={o.l} type="button" onClick={() => setFilters({ ...filters, has_email: o.v })}
                className={`py-1.5 rounded border ${filters.has_email === o.v ? "bg-[#DD2A7B] text-white border-[#DD2A7B]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#DD2A7B]"}`}>{o.l}</button>
            ))}
          </div>
        </div>
        <button type="button" onClick={exportCsv} disabled={items.length === 0}
          data-testid="ig-export-csv"
          className="w-full h-9 rounded-lg border border-[var(--al-border)] text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[var(--al-row-alt)] disabled:opacity-50">
          <Download className="w-3.5 h-3.5" /> {isPt ? "Exportar CSV" : "Export CSV"}
        </button>

        <div className="pt-3 border-t border-[var(--al-border)]">
          <h4 className="text-[11px] uppercase tracking-wider font-semibold text-[var(--al-text-muted)] mb-1.5">{isPt ? "Top perfis" : "Top profiles"}</h4>
          <ul className="space-y-1">
            {(stats.top_owners || []).slice(0, 7).map((o) => (
              <li key={o.username}>
                <button type="button" onClick={() => setFilters({ owner: o.username, hashtag: "", has_email: null })}
                  className="w-full text-left text-[12px] text-[var(--al-text)] hover:text-[#DD2A7B] truncate">
                  @{o.username} <span className="text-[var(--al-text-muted)] tabular-nums">· {o.posts}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-wider font-semibold text-[var(--al-text-muted)] mb-1.5">{isPt ? "Top hashtags" : "Top hashtags"}</h4>
          <div className="flex flex-wrap gap-1">
            {(stats.top_hashtags || []).slice(0, 12).map((h) => (
              <button key={h.hashtag} type="button" onClick={() => setFilters({ owner: "", hashtag: h.hashtag, has_email: null })}
                className="text-[10.5px] font-semibold px-2 py-0.5 rounded bg-[var(--al-row-alt)] hover:bg-[#DD2A7B] hover:text-white">
                #{h.hashtag} ({h.n})
              </button>
            ))}
          </div>
        </div>
      </aside>

      <div className="lg:col-span-3 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[15px] font-bold inline-flex items-center gap-2">
            {isPt ? "Resultados" : "Results"}
            <span className="text-[12px] font-medium text-[var(--al-text-muted)] tabular-nums">
              · {items.length} {isPt ? "posts" : "posts"}
            </span>
          </h3>
          <KanbanTodayBadge isPt={isPt} />
        </div>
        {items.length === 0 ? (
          <div className="al-card p-10 text-center text-[var(--al-text-muted)]">
            {isPt ? "Nenhum post ainda. Vá em 'Coletar' para extrair dados de perfis ou hashtags." : "No posts yet. Use 'Scrape' to extract from profiles or hashtags."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map((p) => (
              <div key={p.id} className="al-card overflow-hidden flex flex-col">
                {p.display_url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer"
                     className="block aspect-square bg-[var(--al-row-alt)] overflow-hidden relative">
                    <img src={`${API}/instagram/proxy-image?url=${encodeURIComponent(p.display_url)}`}
                         alt="" loading="lazy"
                         className="w-full h-full object-cover hover:scale-105 transition-transform"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    {p.type && <span className="absolute top-2 right-2 text-[9px] font-bold uppercase px-2 py-0.5 rounded bg-black/60 text-white">{p.type}</span>}
                  </a>
                )}
                <div className="p-3 flex-1 flex flex-col">
                  <a href={`https://www.instagram.com/${p.owner_username}/`} target="_blank" rel="noopener noreferrer"
                     className="text-[12.5px] font-bold text-[#DD2A7B] hover:underline">@{p.owner_username}</a>
                  {p.owner_full_name && <span className="text-[11px] text-[var(--al-text-muted)] block">{p.owner_full_name}</span>}
                  <p className="text-[12px] mt-1.5 line-clamp-2 text-[var(--al-text)]">{p.caption || (isPt ? "(sem legenda)" : "(no caption)")}</p>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--al-text-muted)] tabular-nums">
                    <span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" /> {(p.likes || 0).toLocaleString()}</span>
                    <span className="inline-flex items-center gap-0.5"><MessageCircle className="w-3 h-3" /> {(p.comments || 0).toLocaleString()}</span>
                    {p.video_views > 0 && <span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" /> {p.video_views.toLocaleString()}</span>}
                  </div>
                  {p.extracted_emails && p.extracted_emails.length > 0 && (
                    <div className="mt-1.5 text-[11px] text-emerald-600 font-bold flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {p.extracted_emails[0]}
                    </div>
                  )}
                  {p.hashtags && p.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {p.hashtags.slice(0, 3).map((h) => (
                        <span key={h} className="text-[9.5px] text-[#8134AF] bg-[#F3E8FF] rounded px-1.5">#{h}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1 mt-auto pt-2">
                    <button type="button" onClick={() => promote(p)}
                      data-testid={`ig-promote-${p.id}`}
                      title={isPt ? "Converter em lead" : "Convert to lead"}
                      className="flex-1 h-7 rounded text-[10.5px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white inline-flex items-center justify-center gap-1">
                      <UserPlus className="w-3 h-3" /> {isPt ? "Lead" : "Lead"}
                    </button>
                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                       className="h-7 px-2 rounded text-[10.5px] font-bold border border-[var(--al-border)] hover:bg-[var(--al-row-alt)] inline-flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> IG
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Runs tab -------------------------------------------------------------
function RunsTab({ isPt, onIngested }) {
  const [runId, setRunId] = useState("256CVrvJjkuZD3wbY");
  const [info, setInfo] = useState(null);
  const [log, setLog] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!runId) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/instagram/runs/${runId}`);
      setInfo(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const fetchLog = async () => {
    if (!runId) return;
    const { data } = await axios.get(`${API}/instagram/runs/${runId}/log?tail=4000`);
    setLog(data.log || "");
  };
  const ingest = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/instagram/runs/${runId}/items`, { params: { persist: true, limit: 200 } });
      toast.success(isPt ? `${data.inserted} novos posts ingeridos` : `${data.inserted} new posts ingested`);
      if (onIngested) onIngested();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const abort = async () => {
    if (!window.confirm(isPt ? "Abortar esta execução?" : "Abort this run?")) return;
    await axios.post(`${API}/instagram/runs/${runId}/abort`);
    refresh();
  };
  const resurrect = async () => {
    await axios.post(`${API}/instagram/runs/${runId}/resurrect`);
    toast.success(isPt ? "Execução retomada" : "Run resurrected");
    refresh();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="ig-runs">
      <div className="al-card p-5 space-y-3 lg:col-span-1">
        <h3 className="text-[14px] font-bold inline-flex items-center gap-1.5"><Play className="w-4 h-4 text-[#DD2A7B]" /> {isPt ? "Inspecionar execução" : "Inspect run"}</h3>
        <input type="text" value={runId} onChange={(e) => setRunId(e.target.value)}
          placeholder="run_id..."
          data-testid="ig-run-id"
          className="w-full al-input h-9 text-[12.5px]" />
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={refresh} disabled={busy}
            data-testid="ig-run-status"
            className="h-9 px-3 rounded-lg bg-[#DD2A7B] hover:bg-[#c11e6a] text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {isPt ? "Status" : "Status"}
          </button>
          <button type="button" onClick={ingest} disabled={busy}
            data-testid="ig-run-ingest"
            className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> {isPt ? "Ingerir" : "Ingest"}
          </button>
          <button type="button" onClick={fetchLog} className="h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[var(--al-row-alt)]">
            <FileText className="w-3.5 h-3.5" /> Log
          </button>
          <button type="button" onClick={abort} className="h-9 px-3 rounded-lg border border-rose-300 text-rose-600 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 hover:bg-rose-50">
            <Square className="w-3.5 h-3.5" /> {isPt ? "Abortar" : "Abort"}
          </button>
          <button type="button" onClick={resurrect} className="h-9 px-3 rounded-lg border border-amber-300 text-amber-600 text-[12px] font-bold col-span-2 inline-flex items-center justify-center gap-1.5 hover:bg-amber-50">
            <Play className="w-3.5 h-3.5" /> {isPt ? "Reanimar (resurrect)" : "Resurrect"}
          </button>
        </div>
        {info && (
          <div className="text-[11px] space-y-1 pt-3 border-t border-[var(--al-border)]">
            <div><b>Status:</b> <span className={info.status === "SUCCEEDED" ? "text-emerald-600 font-bold" : info.status === "FAILED" ? "text-rose-600 font-bold" : "text-amber-600 font-bold"}>{info.status}</span></div>
            <div><b>Started:</b> {info.started_at}</div>
            <div><b>Finished:</b> {info.finished_at || "—"}</div>
            <div><b>Dataset:</b> <code className="text-[10px]">{info.dataset_id}</code></div>
            <div><b>Usage:</b> ${info.usage_usd || 0}</div>
          </div>
        )}
      </div>

      <div className="lg:col-span-2 al-card p-5">
        <h3 className="text-[14px] font-bold mb-2">Log</h3>
        {log ? (
          <pre className="bg-[#0a0a0a] text-[#a3e635] text-[10.5px] leading-relaxed p-3 rounded max-h-[480px] overflow-auto font-mono whitespace-pre-wrap">{log}</pre>
        ) : (
          <p className="text-[12px] text-[var(--al-text-muted)] italic">{isPt ? "Clique em Log para ver." : "Click Log to view."}</p>
        )}
      </div>
    </div>
  );
}
